'use client';

import { createClient } from './client';
import { useState, useEffect, useCallback } from 'react';
import type {
  Profile,
  Coach,
  Lead,
  Message,
  Notification,
  CoachWithProfile,
} from './types';

// ============================================
// useAuth — sjekk innlogget bruker og rolle
// ============================================
export function useAuth() {
  const [user, setUser] = useState<{
    id: string;
    email: string;
    role: 'admin' | 'coach' | 'client';
    profile: Profile | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    let avbrutt = false;

    // Slår opp rollen og setter brukeren. Feiler profil-oppslaget, beholder vi
    // brukeren som innlogget med rolle 'client' — å kaste noen ut til
    // innloggingsskjermen fordi ETT oppslag glapp er verre enn en for snever
    // rolle, som bare betyr at feil panel vises til neste lasting.
    const settBruker = async (sesjonsbruker: { id: string; email?: string }) => {
      let profile: Profile | null = null;
      try {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', sesjonsbruker.id)
          .single();
        profile = data || null;
      } catch {
        profile = null;
      }
      if (avbrutt) return;
      setUser({
        id: sesjonsbruker.id,
        email: sesjonsbruker.email || '',
        role: (profile?.role as 'admin' | 'coach' | 'client') || 'client',
        profile,
      });
      setLoading(false);
    };

    // INITIAL_SESSION fyres alltid av supabase-js når klienten er ferdig
    // initialisert — også når det ikke finnes noen sesjon. Det er den
    // pålitelige inngangen; getSession() ved siden av er bare en snarvei.
    //
    // TIDLIGERE FEIL (fikset 28.8): getSession() ble kappløpt mot en
    // 5-sekunders timeout, og timeouten FEILET LUKKET — tok oppslaget lengre
    // tid, satte catch-en user=null og /coach viste innloggingsskjermen selv
    // med gyldig sesjon. Symptomet var lunefullt: første lasting gikk bra,
    // neste ikke. En vakt som logger deg ut når den er i tvil, er verre enn
    // ingen vakt; her venter vi i stedet på at klienten faktisk blir ferdig.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (avbrutt) return;
        // setTimeout(…, 0) er IKKE kosmetikk: callbacken kjøres mens
        // supabase-js holder auth-låsen, og starter man et Supabase-kall
        // herfra, låser klienten seg selv. Låsen blir stående «held» uten noe
        // «pending», getSession() svarer aldri, og brukeren havner på
        // innloggingsskjermen med gyldig sesjon. Målt i prod 28.8 — og det
        // forklarer hvorfor feilen var lunefull: den avhang av hvem som rakk
        // låsen først. Alt arbeid skyves derfor ut av callbackens kall-ramme.
        if (session?.user) {
          const bruker = session.user;
          setTimeout(() => { if (!avbrutt) void settBruker(bruker); }, 0);
        } else if (event === 'INITIAL_SESSION' || event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
        }
      }
    );

    // Snarvei: er sesjonen allerede i lageret, slipper vi å vente på eventet.
    void (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!avbrutt && data.session?.user) await settBruker(data.session.user);
      } catch {
        // Stille — onAuthStateChange er fasit og kommer uansett.
      }
    })();

    return () => {
      avbrutt = true;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const supabase = createClient();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    const supabase = createClient();
    const { error } = await supabase.auth.signOut();
    if (!error) setUser(null);
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    // Bruk vår egen API som velger avsender (coach@ vs klient@) basert på rolle
    try {
      const r = await fetch('/api/auth/request-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}/auth/reset-password`,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        return { error: { message: data.error || 'Kunne ikke sende e-post' } };
      }
      return { error: null };
    } catch (e: any) {
      return { error: { message: e.message || 'Nettverksfeil' } };
    }
  }, []);

  return {
    user,
    loading,
    isAdmin: user?.role === 'admin',
    isCoach: user?.role === 'coach',
    isClient: user?.role === 'client',
    signIn,
    signOut,
    resetPassword,
  };
}

// ============================================
// useCoaches — hent alle coacher (admin) eller kun aktive (public)
// ============================================
export function useCoaches(options?: { all?: boolean }) {
  const [coaches, setCoaches] = useState<CoachWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCoaches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      let query = supabase
        .from('coaches')
        .select(`
          *,
          profiles (*),
          coach_services (*),
          coach_prices (*),
          coach_education (*),
          coach_experience (*)
        `);

      // Kun aktive coacher for public, alle for admin
      if (!options?.all) {
        query = query.eq('status', 'aktiv');
      }

      const { data, error: fetchError } = await query.order('created_at', { ascending: false });

      if (fetchError) {
        setError(fetchError.message);
      } else {
        setCoaches((data as unknown as CoachWithProfile[]) || []);
      }
    } catch {
      setError('Kunne ikke hente coacher');
    } finally {
      setLoading(false);
    }
  }, [options?.all]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches]);

  return { coaches, loading, error, refetch: fetchCoaches };
}

// ============================================
// useLeads — hent leads (filtrert for coach, alle for admin)
// ============================================
export function useLeads(coachId?: string) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      let query = supabase
        .from('leads')
        .select('*')
        .order('received_at', { ascending: false });

      if (coachId) {
        query = query.eq('coach_id', coachId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        setError(fetchError.message);
      } else {
        setLeads((data as Lead[]) || []);
      }
    } catch {
      setError('Kunne ikke hente leads');
    } finally {
      setLoading(false);
    }
  }, [coachId]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateLead = useCallback(async (leadId: number, updates: Partial<Lead>) => {
    const supabase = createClient();
    const { error: updateError } = await supabase
      .from('leads')
      .update(updates)
      .eq('id', leadId);

    if (!updateError) {
      setLeads(prev =>
        prev.map(l => (l.id === leadId ? { ...l, ...updates } : l))
      );
    }
    return { error: updateError };
  }, []);

  return { leads, loading, error, refetch: fetchLeads, updateLead };
}

// ============================================
// useProfile — hent og oppdater egen profil
// ============================================
export function useProfile() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [coach, setCoach] = useState<Coach | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        setLoading(false);
        return;
      }

      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profileData) {
        setProfile(profileData as Profile);

        // Hent coach-data hvis brukeren er coach
        if (profileData.role === 'coach') {
          const { data: coachData } = await supabase
            .from('coaches')
            .select('*')
            .eq('id', session.user.id)
            .single();
          setCoach((coachData as Coach) || null);
        }
      }

      setLoading(false);
    };

    fetchProfile();
  }, []);

  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!profile) return { error: { message: 'Ikke innlogget' } };

    const supabase = createClient();
    const { error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', profile.id);

    if (!error) {
      setProfile(prev => prev ? { ...prev, ...updates } : null);
    }
    return { error };
  }, [profile]);

  const updateCoach = useCallback(async (updates: Partial<Coach>) => {
    if (!coach) return { error: { message: 'Ikke en coach' } };

    const supabase = createClient();
    const { error } = await supabase
      .from('coaches')
      .update(updates)
      .eq('id', coach.id);

    if (!error) {
      setCoach(prev => prev ? { ...prev, ...updates } : null);
    }
    return { error };
  }, [coach]);

  return { profile, coach, loading, updateProfile, updateCoach };
}

// ============================================
// useMessages — hent meldinger med realtime
// ============================================
export function useMessages(conversationId: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!conversationId) {
      setLoading(false);
      return;
    }

    const supabase = createClient();

    // Hent eksisterende meldinger
    const fetchMessages = async () => {
      const { data } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      setMessages((data as Message[]) || []);
      setLoading(false);
    };

    fetchMessages();

    // Lytt pa nye meldinger i sanntid
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          setMessages(prev => [...prev, payload.new as Message]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          setMessages(prev =>
            prev.map(m => (m.id === (payload.new as Message).id ? payload.new as Message : m))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  const sendMessage = useCallback(async (content: string, senderId: string) => {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        content,
        message_type: 'text',
      })
      .select()
      .single();

    if (!error && data) {
      // Oppdater last_message_at pa samtalen
      await supabase
        .from('conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', conversationId);
    }

    return { data, error };
  }, [conversationId]);

  const markAsRead = useCallback(async (messageId: string) => {
    const supabase = createClient();
    await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('id', messageId)
      .is('read_at', null);
  }, []);

  return { messages, loading, sendMessage, markAsRead };
}

// ============================================
// useNotifications — hent uleste notifikasjoner
// ============================================
export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const fetchNotifications = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      const notifs = (data as Notification[]) || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
      setLoading(false);
    };

    fetchNotifications();

    // Lytt pa nye notifikasjoner
    const setupRealtime = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const channel = supabase
        .channel(`notifications:${session.user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${session.user.id}`,
          },
          (payload) => {
            const newNotif = payload.new as Notification;
            setNotifications(prev => [newNotif, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        )
        .subscribe();

      return channel;
    };

    let channelRef: ReturnType<typeof supabase.channel> | undefined;
    setupRealtime().then(ch => { channelRef = ch; });

    return () => {
      if (channelRef) {
        supabase.removeChannel(channelRef);
      }
    };
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    const supabase = createClient();
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (!error) {
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', session.user.id)
      .eq('read', false);

    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }, []);

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead };
}
